import * as React from "react";
import {
  useState,
  ChangeEvent,
  useContext,
  useCallback,
  Dispatch,
  SetStateAction,
  useEffect,
} from "react";
import SuccessUploader from "./success";
import EmptyUploader from "./empty";
import { useDropzone, FileRejection } from "react-dropzone";
import { useReactiveStyle } from "./useReactiveStyle";
import { UploadApi } from "../../../api/commercial/document-upload/api";
import {
  AttachmentMetaDocument,
  BasicAccountApplicationDocument,
  CardApplicationDocument,
  CompanyAttachment,
  PersonAttachment,
} from "../../../api/commercial/generated-types";
import ErrorUploader from "./error";
import { AttachmentUploadContext } from "../adapter";
import "./styles.scss";
import { useMessageSource } from "react-message-source";
import { UploadHelper } from "../upload-helper";
import { StatusCodes } from "http-status-codes";
import { useStopBubbling } from "../../../ui/widgets";
import NetworkError from "../../../api/http/networkError.tsx";

export interface UploaderProps {
  dataForUploading: {
    applicationRef:
      | BasicAccountApplicationDocument["applicationRef"]
      | CardApplicationDocument["applicationRef"];
    attachmentType: CompanyAttachment["domain"] | PersonAttachment["domain"];
  };
  preloadedUpload: AttachmentMetaDocument;
  uploadInProgress: {
    isUploadInProgress: boolean;
    setIsUploadInProgress: Dispatch<SetStateAction<boolean>>;
  };
}

export type ErrorBag = {
  error: string;
};

const classNameBase = "scd-upload";

const Uploader = ({
  dataForUploading,
  preloadedUpload,
  uploadInProgress,
}: UploaderProps): React.JSX.Element => {
  const { getMessage } = useMessageSource();

  const [uploadedFile, setUploadedFile] = useState<
    AttachmentMetaDocument | ErrorBag
  >(preloadedUpload);

  const [uploadClass, setUploadClass] = useState<string>("");
  useEffect(() => {
    setUploadClass("");
    if (!uploadInProgress.isUploadInProgress) {
      if (UploadHelper.isUploadFailed(uploadedFile)) {
        setUploadClass(`${classNameBase}-error`);
      } else if (UploadHelper.isUploadSuccessful(uploadedFile)) {
        setUploadClass(`${classNameBase}-success`);
      }
    }
  }, [uploadedFile, uploadInProgress]);

  const {
    maxFileSize,
    dropzoneAllowedMimeTypes,
    syncAttachmentStateWithBackend,
  } = useContext(AttachmentUploadContext);

  const uploadFileToServer = useCallback(
    async (file: File): Promise<void> => {
      try {
        uploadInProgress.setIsUploadInProgress(true);
        const attachmentMeta = await UploadApi.upload(
          file,
          dataForUploading.applicationRef,
          dataForUploading.attachmentType,
        );
        setUploadedFile(attachmentMeta);
        syncAttachmentStateWithBackend();
      } catch (e) {
        if ((e as NetworkError)?.status === StatusCodes.REQUEST_TOO_LONG) {
          setUploadedFile({
            error: getMessage(
              "DocumentUpload.error.file-too-large",
              UploadHelper.calculateFileSizeInMB(maxFileSize),
            ),
          });
          return;
        }
        setUploadedFile({
          error: getMessage("DocumentUpload.error.generic"),
        });
      } finally {
        uploadInProgress.setIsUploadInProgress(false);
      }
    },
    [
      uploadInProgress,
      dataForUploading.applicationRef,
      dataForUploading.attachmentType,
      syncAttachmentStateWithBackend,
      getMessage,
      maxFileSize,
    ],
  );

  const {
    open,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    maxSize: maxFileSize,
    accept: dropzoneAllowedMimeTypes,
    multiple: false,
    onDropAccepted: useCallback(
      async (acceptedFiles: File[]): Promise<void> => {
        if (UploadHelper.isUploadSuccessful(uploadedFile)) {
          await UploadApi.remove(
            dataForUploading.applicationRef,
            uploadedFile.uuid,
          );
        }
        uploadFileToServer(acceptedFiles[0]);
      },
      [dataForUploading.applicationRef, uploadFileToServer, uploadedFile],
    ),
    onDropRejected: useCallback(
      (fileRejections: FileRejection[]) => {
        if (fileRejections[0].errors[0].code === "file-too-large") {
          setUploadedFile({
            error: getMessage(
              "DocumentUpload.error.file-too-large",
              UploadHelper.calculateFileSizeInMB(maxFileSize),
            ),
          });
        }
      },
      [maxFileSize, getMessage],
    ),
  });

  const handleOnClickFileUploader = useStopBubbling(open);

  // Called only when file is added.
  const handleOnUploadedFile = (e: ChangeEvent<HTMLInputElement>): void => {
    uploadFileToServer(e.target.files[0]);
  };

  const handleOnClickRemoveFile = useStopBubbling(async (): Promise<void> => {
    await UploadApi.remove(
      dataForUploading.applicationRef,
      (uploadedFile as AttachmentMetaDocument).uuid,
    );
    setUploadedFile(null);
    syncAttachmentStateWithBackend();
  });

  const renderHiddenFileUploader = (): React.JSX.Element => (
    <input
      {...getInputProps()}
      type="file"
      data-testid="file"
      onChange={handleOnUploadedFile}
      multiple={false}
      style={{ display: "none" }}
    />
  );

  return (
    <div className={[classNameBase, uploadClass].join(" ")}>
      {renderHiddenFileUploader()}
      <div
        className={`${classNameBase}-item`}
        {...getRootProps({
          style: useReactiveStyle(isDragActive, isDragAccept, isDragReject),
        })}
      >
        <>
          {UploadHelper.isUploadSuccessful(uploadedFile) &&
            !uploadInProgress.isUploadInProgress && (
              <SuccessUploader
                handleOnClickRemoveFile={handleOnClickRemoveFile}
                uploadedFile={uploadedFile}
              />
            )}
          {(!uploadedFile || uploadInProgress.isUploadInProgress) && (
            <EmptyUploader
              handleOnClickFileUploader={handleOnClickFileUploader}
              isDropActive={isDragActive || isDragAccept || isDragReject}
              isUploadInProgress={uploadInProgress.isUploadInProgress}
            />
          )}
          {UploadHelper.isUploadFailed(uploadedFile) &&
            !uploadInProgress.isUploadInProgress && (
              <ErrorUploader
                handleOnClickFileUploader={handleOnClickFileUploader}
                isDropActive={isDragActive || isDragAccept || isDragReject}
                error={uploadedFile.error}
              />
            )}
        </>
      </div>
    </div>
  );
};

export default Uploader;
